import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { fmtTRY, fmtDate, fmtQty4 } from "../lib/format";

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(!!sessionId);
  const tracked = useRef(false);

  useEffect(() => {
    if (order && !tracked.current) {
      tracked.current = true;
      trackEvent("purchase", { transaction_id: order.order_number, currency: "TRY", value: order.total, items: (order.items || []).map((i) => ({ item_id: i.product_id, item_name: i.name, price: i.price ?? i.total, quantity: i.quantity })) });
    }
  }, [order]);

  useEffect(() => {
    const email = sessionStorage.getItem(`order_${orderNumber}`) || "";
    const load = () => api.get(`/orders/${orderNumber}${email ? `?email=${encodeURIComponent(email)}` : ""}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setError("Sipariş bilgisi yüklenemedi"));

    if (sessionId) {
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") { clearInterval(poll); setPaying(false); load(); }
          else if (tries > 8) { clearInterval(poll); setPaying(false); load(); }
        } catch { if (tries > 8) { clearInterval(poll); setPaying(false); load(); } }
      }, 2000);
      return () => clearInterval(poll);
    }
    load();
  }, [orderNumber, sessionId]);

  if (error) return <div className="max-w-xl mx-auto px-4 py-24 text-center"><p className="font-serif text-2xl text-stone-800">{error}</p><Link to="/" className="text-forest font-semibold mt-4 inline-block">Ana sayfaya dön</Link></div>;
  if (!order) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-stone-500"><Loader2 className="w-8 h-8 animate-spin mx-auto text-forest" /><p className="mt-4">{paying ? "Ödemeniz doğrulanıyor..." : "Yükleniyor..."}</p></div>;

  const payLabel = { havale: "Havale / EFT", kapida: "Kapıda Ödeme", stripe: "Kredi Kartı" }[order.payment_method] || order.payment_method;

  return (
    <div data-testid="order-success-page" className="max-w-3xl mx-auto px-4 py-14">
      <div className="bg-white rounded-3xl border border-stone-200/80 p-8 sm:p-12 text-center shadow-sm">
        <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
        <h1 className="font-serif text-3xl text-forest mt-5">Siparişiniz Alındı</h1>
        <p data-testid="order-number" className="mt-3 text-lg font-mono font-bold text-stone-900">{order.order_number}</p>
        <p className="text-stone-500 text-sm mt-1">{fmtDate(order.created_at)} · {payLabel}</p>
        <p data-testid="payment-status" className="mt-2 text-sm font-semibold">
          {order.payment_status === "paid" ? <span className="text-green-700">Ödeme alındı</span> : <span className="text-amber-700">Ödeme bekleniyor</span>}
        </p>
        {order.payment_method === "havale" && order.payment_status !== "paid" && (
          <div className="mt-6 bg-cream-dark/60 rounded-xl p-5 text-left text-sm">
            <p className="font-semibold text-stone-900">Havale/EFT Bilgileri</p>
            <p className="text-stone-600 mt-2">Açıklama kısmına sipariş numaranızı (<b>{order.order_number}</b>) yazınız. Ödemeniz onaylandıktan sonra siparişiniz kargoya verilir.</p>
          </div>
        )}
        <div className="mt-8 text-left border-t border-stone-100 pt-6">
          {order.items.map((i) => (
            <div key={i.product_id + (i.variant || "")} className="flex justify-between text-sm py-2">
              <span className="text-stone-700">{i.name}{i.variant ? ` (${i.variant})` : ""} × {fmtQty4(i.quantity, i.unit || "")}</span>
              <span className="font-medium">{fmtTRY(i.total)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm py-2 border-t border-stone-100 mt-2"><span className="text-stone-500">Kargo</span><span>{order.shipping_cost === 0 ? "Ücretsiz" : fmtTRY(order.shipping_cost)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-sm py-2 text-green-700"><span>İndirim</span><span>-{fmtTRY(order.discount)}</span></div>}
          <div className="flex justify-between font-bold text-lg pt-3 border-t border-stone-200"><span>Toplam</span><span className="text-forest">{fmtTRY(order.total)}</span></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <Link to="/urunler" className="bg-forest hover:bg-forest-dark text-white font-semibold px-7 py-3 rounded-xl transition-colors">Alışverişe Devam Et</Link>
          <a href={`https://wa.me/905551112233?text=${encodeURIComponent(`Merhaba, ${order.order_number} numaralı siparişim hakkında bilgi almak istiyorum.`)}`} target="_blank" rel="noreferrer" className="bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold px-7 py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
            <MessageCircle className="w-4 h-4" /> WhatsApp Destek
          </a>
        </div>
      </div>
    </div>
  );
}
